import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgSelectComponent } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';

import { TimelineQuery } from '@models/milestone-category/milestone/timeline.query';
import { TrialInsightsQuery } from '@models/trial-insights/trial-insights.query';
import { TrialInsightsStore } from '@models/trial-insights/trial-insights.store';

import { ROUTING_PATH } from '@shared/constants/routingPath';
import { Option } from '@shared/types/components.type';
import { Utils } from '@shared/utils/utils';

import { DEFAULT_ANALYTIC_CARDS } from './analytic-card.const';
import {
  AnalyticData,
  TrialInsightsFinanceAnalyticCardComponent,
} from './trial-insights-finance-analytic-card.component';
import { TrialInsightsFinanceVariancesService } from './trial-insights-finance-variances.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    TrialInsightsFinanceAnalyticCardComponent,
    RouterLink,
    AsyncPipe,
  ],
  selector: 'aux-trial-insights-finance-comparing-budget',
  templateUrl: './trial-insights-finance-comparing-budget.component.html',
})
export class TrialInsightsFinanceComparingBudgetComponent implements OnDestroy, OnInit {
  private changeDetector = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private timelineQuery = inject(TimelineQuery);
  private trialInsightsFinanceVariancesService = inject(TrialInsightsFinanceVariancesService);
  private trialInsightsQuery = inject(TrialInsightsQuery);
  private trialInsightsStore = inject(TrialInsightsStore);

  toDateControl = new FormControl('');
  trialRangeDate$: Observable<Option[]> = this.timelineQuery
    .selectTimelineRange('MMM YYYY')
    .pipe(map((range) => range.map((date) => ({ label: date, value: date }))));

  fromRange$ = combineLatest([this.trialRangeDate$, this.toDateControl.valueChanges]).pipe(
    map(([trialRange, toDate]) => {
      return trialRange.filter(({ value }) => {
        return toDate === value ? true : dayjs(value).isBefore(dayjs(toDate));
      });
    })
  );

  fromDateControl = new FormControl('');

  toRange$ = combineLatest([this.trialRangeDate$, this.fromDateControl.valueChanges]).pipe(
    map(([trialRange, fromDate]) => {
      return trialRange.filter(({ value }) => {
        return fromDate === value ? true : dayjs(value).isAfter(dayjs(fromDate));
      });
    })
  );

  analyticCards$ = new BehaviorSubject<AnalyticData[]>(DEFAULT_ANALYTIC_CARDS);
  budgetLink = `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.BUDGET.INDEX}`;
  currentOpenMonth$ = this.trialInsightsQuery.select('currentOpenMonth').pipe(
    map((currentOpenMonth) => {
      if (!currentOpenMonth) {
        return '';
      }

      return currentOpenMonth ? dayjs(currentOpenMonth).format('MMM YYYY') : '';
    })
  );
  fromRange: Option[] = [];
  snapshotName$ = this.trialInsightsQuery
    .select((state) => state.bvaChart.snapshotName)
    .pipe(
      map((snapshotName) => {
        return snapshotName || Utils.zeroHyphen;
      })
    );
  toRange: Option[] = [];

  ngOnInit(): void {
    this.fetchVariances();

    this.fromRange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.fromRange = value;
      this.changeDetector.detectChanges();
    });
    this.toRange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.toRange = value;
      this.changeDetector.detectChanges();
    });

    // SET DEFAULT DATES
    combineLatest([this.trialRangeDate$, this.currentOpenMonth$])
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged(isEqual))
      .subscribe(([timeline, currentOpenMonth]) => {
        if (currentOpenMonth && timeline[0]) {
          this.fromDateControl.setValue(timeline[0].value);

          const value = dayjs(currentOpenMonth).subtract(1, 'month');

          // check if subtracted date in timeline range
          const validDate = value.isAfter(timeline[0].value)
            ? value.format('MMM YYYY')
            : timeline[0].value;

          this.toDateControl.setValue(validDate);
        }
      });
  }

  ngOnDestroy() {
    this.trialInsightsStore.reset();
  }

  private fetchVariances() {
    combineLatest([
      this.trialInsightsQuery.select((state) => state.bvaChart.vendorId),
      this.snapshotName$,
      this.fromDateControl.valueChanges,
      this.toDateControl.valueChanges,
    ])
      .pipe(
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
        tap(([, snapshotName]) => {
          if (snapshotName === Utils.zeroHyphen || !snapshotName) {
            this.analyticCards$.next(DEFAULT_ANALYTIC_CARDS);
          }
        }),
        filter(([, snapshotName, fromDate, toDate]) => {
          return !!snapshotName && snapshotName !== Utils.zeroHyphen && !!toDate && !!fromDate;
        }),
        switchMap(([vendorId, snapshotName, fromDate, toDate]) => {
          return this.trialInsightsFinanceVariancesService.getListActivityVariances(
            vendorId || null,
            snapshotName,
            fromDate!,
            toDate!
          );
        })
      )
      .subscribe((analyticCards) => {
        this.analyticCards$.next(analyticCards);
      });
  }
}
